import { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material'
import {
    Search as SearchIcon,
    VisibilityOutlined as ImpersonateIcon,
    Close as CloseIcon,
    Factory as FactoryIcon,
    Storefront as StorefrontIcon,
} from '@mui/icons-material'
import { useEmulation } from '../../context/AuthenticationContext'
import { getManufacturers } from '../../apiRoutes/manufacturers'
import { getDealerProfiles } from '../../apiRoutes/dealerProfileRoutes'

const asArray = (r: any) =>
    Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []
const idOf = (x: any) => (x && typeof x === 'object' ? x._id : x)

/**
 * Impersonation picker: a super admin chooses a Manufacturer or Dealer Profile
 * to impersonate (read-only). Selecting one enters faithful impersonation of
 * that tier — scoped exactly as that manufacturer / dealer profile.
 */
export const ImpersonateDialog = ({
    open,
    onClose,
}: {
    open: boolean
    onClose: () => void
}) => {
    const { setEmulation } = useEmulation()
    const [tab, setTab] = useState(0)
    const [q, setQ] = useState('')
    const [loading, setLoading] = useState(false)
    const [manufacturers, setManufacturers] = useState<any[]>([])
    const [profiles, setProfiles] = useState<any[]>([])

    useEffect(() => {
        if (!open) return
        let cancelled = false
        setLoading(true)
        Promise.all([getManufacturers(true), getDealerProfiles(true)])
            .then(([m, d]) => {
                if (cancelled) return
                setManufacturers(asArray(m))
                setProfiles(asArray(d))
            })
            .finally(() => !cancelled && setLoading(false))
        return () => {
            cancelled = true
        }
    }, [open])

    const term = q.trim().toLowerCase()
    const items = useMemo(() => {
        const named: any[] =
            tab === 0
                ? manufacturers.map((x: any) => ({
                      id: x._id,
                      name: x.manufacturerName,
                      sub: '',
                      payload: {
                          type: 'manufacturer',
                          id: x._id,
                          name: x.manufacturerName,
                      },
                  }))
                : profiles.map((x: any) => ({
                      id: x._id,
                      name: x.dealerProfileName || x.dealerName,
                      sub: x.dealerName || '',
                      payload: {
                          type: 'dealer',
                          id: x._id,
                          name: x.dealerProfileName || x.dealerName,
                          dealers: (x.dealers || []).map(idOf),
                          manufacturers: (x.manufacturers || []).map(idOf),
                      },
                  }))
        const filtered = term
            ? named.filter((x) => (x.name || '').toLowerCase().includes(term))
            : named
        return filtered
            .filter((x) => x.name)
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [tab, manufacturers, profiles, term])

    const choose = (x: any) => {
        setEmulation(x.payload)
        onClose()
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}
            >
                <ImpersonateIcon sx={{ color: 'primary.main' }} />
                Impersonate
                <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', ml: 0.5 }}
                >
                    read-only
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Tabs
                    value={tab}
                    onChange={(_e, v) => setTab(v)}
                    sx={{
                        mb: 1,
                        minHeight: 38,
                        '& .MuiTab-root': {
                            minHeight: 38,
                            textTransform: 'none',
                        },
                    }}
                >
                    <Tab
                        icon={<FactoryIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Manufacturers"
                    />
                    <Tab
                        icon={<StorefrontIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Dealer Profiles"
                    />
                </Tabs>
                <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder={`Search ${
                        tab === 0 ? 'manufacturers' : 'dealer profiles'
                    }…`}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{ fontSize: 18, color: 'text.disabled' }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />
                {loading ? (
                    <Box
                        sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                    >
                        <CircularProgress size={22} />
                    </Box>
                ) : (
                    <List sx={{ maxHeight: 360, overflow: 'auto', mt: 0.5 }}>
                        {items.length === 0 && (
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.disabled', px: 1, py: 2 }}
                            >
                                No matches.
                            </Typography>
                        )}
                        {items.map((x) => (
                            <ListItemButton
                                key={x.id}
                                onClick={() => choose(x)}
                                sx={{ borderRadius: 1 }}
                            >
                                <ListItemText
                                    primary={x.name}
                                    secondary={x.sub || undefined}
                                    primaryTypographyProps={{ fontSize: 14 }}
                                    secondaryTypographyProps={{ fontSize: 11 }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    )
}

/**
 * Floating bottom-center pill shown while impersonating, with an Exit action.
 * Fixed position so it never disturbs page layout.
 */
export const ImpersonationBanner = () => {
    const { isEmulating, emulation, exitEmulation } = useEmulation()
    if (!isEmulating) return null
    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1400,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                color: 'common.white',
                bgcolor: '#6D28D9',
                boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
                border: '1px solid rgba(255,255,255,0.25)',
            }}
        >
            <ImpersonateIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Impersonating&nbsp;
                <b>{emulation?.name}</b>
                <Box
                    component="span"
                    sx={{ opacity: 0.75, ml: 0.75, fontWeight: 400 }}
                >
                    ·{' '}
                    {emulation?.type === 'dealer'
                        ? 'dealer profile'
                        : 'manufacturer'}{' '}
                    · read-only
                </Box>
            </Typography>
            <Chip
                icon={
                    <CloseIcon
                        sx={{ fontSize: 16, color: 'inherit !important' }}
                    />
                }
                label="Exit"
                size="small"
                onClick={exitEmulation}
                sx={{
                    ml: 0.5,
                    color: 'common.white',
                    bgcolor: 'rgba(255,255,255,0.18)',
                    fontWeight: 700,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
            />
        </Box>
    )
}
